import { Button, Checkbox, Form, Input, Select, Spin } from "antd";
import axios from "axios";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { api, endpoint } from "@/api";
import appConfig from "@/config/appConfig";
import { userRole, userRoleFormate } from "@/constant/userRole";
import { useSearchQuery } from "@/utils/useSearchQuery";
import { validateFields } from "@/utils/validation";
import PropTypes from "prop-types";
import { flare } from "@flareapp/js";

const { admin, businessAdmin, installer, public: publicUser, user } = userRole;

const formSchema = {
  name: {
    type: "string",
    required: true,
  },
  email: {
    type: "string",
    required: true,
  },
  phone: {
    type: "string",
    required: true,
  },
  fax: {
    type: "string",
    required: false,
  },
  role: {
    type: "string",
    required: true,
  },
};

const getFormSchema = (mode, role) => {
  let schema = {};
  if (mode === "create") {
    schema = {
      ...formSchema,
      password: {
        type: "string",
        required: true,
      },
      cpassword: {
        type: "string",
        required: true,
        match: "password",
        message: "Confirm Password did not match",
      },
    };
  }
  if (["user", "public", "admin"].includes(role)) {
    schema = {
      ...formSchema,
      site: { type: "array", minLength: 1 },
    };
  } else if (["admin", "installer"].includes(role)) {
    schema = {
      ...formSchema,
      companyName: { type: "string", required: true },
      companyAddress: { type: "string", required: true },
    };
  }
  return schema;
};

const UserForm = ({ mode = "create", user: userData = {}, onClickChangePwd }) => {
  const navigate = useNavigate();
  const uRole = useSearchQuery("role");
  const [form] = Form.useForm();
  const loggedInUser = useSelector((state) => state?.user?.userDetails);
  const [warnMsg, setWarnMsg] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState([]);
  const [role, setRole] = useState(uRole);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [siteLocations, setSiteLocations] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [isLimitOver, setIsLimitOver] = useState(false);

  const getBusiness = async () => {
    setIsLoading(true);
    try {
      const data = await api.get(endpoint.business);
      setBusinesses(data?.data);
      if (Array.isArray(data?.data) && data.data.length === 1) {
        form.setFieldsValue({ business: [data.data[0]?._id] });
        getSiteLocations([data.data[0]?._id]);
        getMySubscription();
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
    setIsLoading(false);
  };

  const getSingleBusiness = async (businessId) => {
    try {
      if (mode !== "edit") {
        const { data } = await api.get(endpoint.business + businessId);
        form.setFieldsValue({
          idleLogoutFot30Min: data?.idleLogoutFot30Min,
          multipleDeviceLogin: data?.multipleDeviceLogin,
          // urlLogin: data?.urlLogin,
        });
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log("business list", error);
    }
  };

  const getSiteLocations = debounce(async (business = []) => {
    try {
      if (!business.length) return;
      const { data } = await api.get(`/site-location`, { params: { business, limit: 100000 } });
      if (Array.isArray(data) && data.length > 0) {
        setSiteLocations(data?.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)));
      } else {
        setSiteLocations([]);
      }
    } catch {
      setSiteLocations([]);
    }
  }, 500);

  const getMySubscription = async () => {
    try {
      const { hasAccess, assignedPlan } = await api.get(endpoint.mySubscription, {
        params: {
          type: role,
          unknown: true,
        },
      });
      if (mode !== "edit") {
        setIsLimitOver(!hasAccess);
        if (!hasAccess) {
          setWarnMsg(
            `${assignedPlan?.business?.name} (${assignedPlan?.business?.id})` +
              " has exceeded the limit for the maximum number of " +
              userRoleFormate[role] +
              "",
          );
        }
      }
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedBusiness) {
      getSingleBusiness(selectedBusiness);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    getBusiness();
  }, [role]);

  useEffect(() => {
    if (mode === "edit" && userData.role) {
      setRole(userData.role);
      form.setFieldsValue({ role: userData.role });
    }
  }, [mode, userData]);

  useEffect(() => {
    if (userData.business) {
      getSiteLocations(userData?.business?.map((s) => (typeof s === "string" ? s : s._id)));
    }
  }, [userData.business, mode]);

  useEffect(() => {
    if (mode === "edit" && userData) {
      document.title = `${appConfig.name} - Edit User`;
      setImageUrl(userData.avatar);
    } else {
      document.title = `${appConfig.name} - Create User`;
    }
  }, [mode, userData]);

  //Profile Photo
  const handleFileUpload = (file) => {
    setImageUrl(URL.createObjectURL(file));
    let form = new FormData();
    form.append("avatar", file);
    setSelectedImage(form);
  };

  const createUser = async (body) => {
    const { values } = validateFields(body, getFormSchema(role, mode));

    if (role === businessAdmin) {
      if (body.business?.length > 0) {
        const siteIds = siteLocations?.map((d) => d._id) || [];
        values.site = siteIds;
      } else {
        values.site = [];
      }
    }

    if (body.password === body.cpassword) {
      setIsLoading(true);
      try {
        const response = await axios.post(`/user`, values, {
          withCredentials: true,
        });
        const data = response.data;
        if (data) {
          if (selectedImage === null) {
            setIsLoading(false);
            await Swal.fire({
              title: "Done!",
              text: "User Created Successfully",
              icon: "success",
              timer: 2000,
              confirmButtonText: "OK",
            });
            navigate(-1);
          } else {
            const addImageResponse = await axios.put(`/user/${data._id}/avatarUpload/`, selectedImage, {
              withCredentials: true,
            });
            if (addImageResponse) {
              setIsLoading(false);
              navigate("/users");
            }
          }
        }
      } catch (error) {
        flare.report(error);
        if (error.response) {
          Swal.fire({
            title: "",
            text: error.response?.data?.message || "Something went wrong",
            icon: "error",
            timer: 2000,
            confirmButtonText: "OK",
          });
        }
      }
    } else {
      Swal.fire({
        title: "",
        text: "Both Passwords Are Not Matching",
        icon: "error",
        timer: 2000,
        confirmButtonText: "OK",
      });
    }
  };
  const editUser = async (body) => {
    const { values } = validateFields(body, getFormSchema(role, mode));

    if (body.role === userRole.businessAdmin) {
      if (body.business?.length > 0) {
        const siteIds = siteLocations?.map((d) => d._id) || [];
        values.site = siteIds;
      } else {
        values.site = [];
      }
    }

    setIsLoading(true);
    try {
      const data = await api.put(`/user/${userData._id}`, values);

      if (data) {
        if (selectedImage === null) {
          setIsLoading(false);
          await Swal.fire({
            title: "Done!",
            text: "User Updated Successfully",
            icon: "success",
            timer: 2000,
            confirmButtonText: "OK",
          });
          navigate(-1);
        } else {
          const addImageResponse = await axios.put(`/user/${userData._id}/avatarUpload/`, selectedImage, {
            withCredentials: true,
          });
          if (addImageResponse) {
            setIsLoading(false);
          }
        }
      }
    } catch (error) {
      flare.report(error);
      if (error.response) {
        Swal.fire({
          title: "",
          text: error.response?.data?.message || "Something went wrong",
          icon: "error",
          timer: 2000,
          confirmButtonText: "OK",
        });
      }
    }
  };

  const getHeader = (role) => {
    switch (role) {
      case "installer":
        return {
          title: mode === "edit" ? "Save Installer" : "Add New Installer",
          btnText: mode === "edit" ? "Save Installer" : "Create Installer",
        };
      case "admin":
        return {
          title: mode === "edit" ? "Save Site Admin" : "Add New Site Admin",
          btnText: mode === "edit" ? "Save Site Admin" : "Create Site Admin",
        };
      case "user":
        return {
          title: mode === "edit" ? "Save Site User" : "Add New Site User",
          btnText: mode === "edit" ? "Save Site User" : "Create Site User",
        };
      case "public":
        return {
          title: mode === "edit" ? "Save Public User" : "Add New Public User",
          btnText: mode === "edit" ? "Save Public User" : "Create Site Public User",
        };
      case "businessAdmin":
        return {
          title: mode === "edit" ? "Save Business Admin" : "Add New Business Admin",
          btnText: mode === "edit" ? "Save Business Admin" : "Create Business Admin",
        };
      default:
        return {
          title: mode === "edit" ? "Save User" : "Add New User",
          btnText: mode === "edit" ? "Save User" : "Create User",
        };
    }
  };

  // antd
  const onFinish = async (values) => {
    if (mode === "create") {
      await createUser(values);
    } else if (mode === "edit") {
      await editUser(values);
    }
    setIsLoading(false);
  };
  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  return (
    <Spin spinning={isLoading}>
      <div className="card p-3 m-md-3">
        <div>
          <label className="az-content-label">Manage Users</label>
          <span className="d-block py-2">{getHeader(role).title}</span>
        </div>
        <hr className="mt-0 mb-4" />
        <Form
          layout="vertical"
          name="users"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          form={form}
          initialValues={
            mode === "edit"
              ? {
                  ...userData,
                  role,
                  site: userData?.site?.map((s) => (typeof s === "string" ? s : s._id)),
                  business: userData?.business[0]?._id,
                }
              : { role }
          }
        >
          <Row>
            <Col md={4}>
              <Form.Item
                label="User Role"
                name="role"
                className="w-100"
                rules={[
                  {
                    required: true,
                    message: "Select a Role",
                  },
                ]}
              >
                <Select
                  disabled={true}
                  showSearch={true}
                  placeholder="Select User Role"
                  filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                  options={[
                    ...Object.entries(userRoleFormate)
                      // eslint-disable-next-line no-unused-vars
                      .filter(([key, value]) => ![userRoleFormate.superAdmin].includes(value))
                      .map(([key, value]) => ({ value: key, label: value })),
                  ]}
                />
              </Form.Item>
            </Col>

            {[businessAdmin, installer, admin, user, publicUser].includes(role) && (
              <Col md={4}>
                <Form.Item
                  label="Assign Business"
                  name="business"
                  className="w-100"
                  rules={[
                    {
                      required: true,
                      message: "Select a Business",
                    },
                  ]}
                  extra={
                    warnMsg && (
                      <p
                        style={{
                          color: "red",
                          marginBottom: 0,
                          marginTop: "2px",
                          display: warnMsg ? "block" : "none",
                        }}
                      >
                        Warning: {warnMsg}
                      </p>
                    )
                  }
                >
                  <Select
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Business"
                    disabled={businesses?.length === 1}
                    options={[...businesses.map((item) => ({ label: `${item.name} (${item.id})`, value: item._id }))]}
                    onChange={(e) => {
                      getSiteLocations(e);
                      setSelectedBusiness(e);
                      form.setFieldsValue({ site: [] });
                    }}
                  />
                </Form.Item>
              </Col>
            )}
            <Col md={4}>
              <Form.Item
                normalize={(value) => value && value.toLowerCase()}
                label="Login ID"
                name="loginId"
                rules={[{ required: true, message: "Enter Login ID" }]}
              >
                <Input placeholder="Enter Login ID (lowercase only)" />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col md={[user, publicUser, admin, installer].includes(role) ? 4 : 6}>
              <Form.Item label="Full Name" name="name" rules={[{ required: true, message: "Enter Full Name" }]}>
                <Input placeholder="Enter Full Name" />
              </Form.Item>
            </Col>

            {[user, publicUser, admin, installer].includes(role) && (
              <Col md={4}>
                <Form.Item
                  label="Assign Site"
                  name="site"
                  rules={[
                    {
                      required: true,
                      message: "Select Sites",
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    name="site"
                    showSearch={true}
                    filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                    placeholder="Select Site"
                    maxTagCount={1}
                    maxTagTextLength={20}
                    options={[...siteLocations.map((item) => ({ label: item.name, value: item._id }))]}
                  />
                </Form.Item>
              </Col>
            )}

            <Col md={[user, publicUser, admin, installer].includes(role) ? 4 : 6}>
              <Form.Item
                label="Email Address"
                name="email"
                rules={[
                  {
                    type: "email",
                    message: "Email is not valid",
                  },
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
          </Row>

          {mode === "create" && (
            <Row>
              <Col md={6}>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Input Your Password!",
                    },
                    { min: 6, message: "Password must be minimum 6 characters." },
                  ]}
                >
                  <Input.Password placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;" />
                </Form.Item>
              </Col>
              <Col md={6}>
                <Form.Item
                  label="Reenter Password"
                  name="cpassword"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: "Renter Your password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Both Passwords Are Not Matching"));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={6}>
              <Form.Item
                label="Phone Number"
                name="phone"
                rules={[
                  {
                    pattern: /^[0-9]+$/,
                    message: "Please Enter Number Only",
                  },
                ]}
              >
                <Input addonBefore={<>+6</>} className="w-100" placeholder="Enter Phone Number" />
              </Form.Item>

              <div className="mb-3">
                <label htmlFor="select-image" className="form-label">
                  Profile Photo
                </label>

                <div className="input-group m-0">
                  <input
                    className="form-control"
                    accept="image/*"
                    type="file"
                    id="select-image"
                    name="image"
                    style={{ display: "none" }}
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                  />
                  <label htmlFor="select-image">
                    <img
                      src={imageUrl || "/images/avatar.png"}
                      alt=""
                      height="100px"
                      className="rounded-3 border p-2"
                    />
                  </label>
                </div>
              </div>
            </Col>

            {loggedInUser?.role === userRole.superAdmin && (
              <Col md={6}>
                <Form.Item
                  layout="horizontal"
                  label="30-minute time-out when idle"
                  name="idleLogoutFot30Min"
                  className="right-label mb-0"
                  labelHeight="24px"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
                <Form.Item
                  layout="horizontal"
                  label="Multiple active login sessions for single account"
                  name="multipleDeviceLogin"
                  className="right-label mb-0"
                  labelHeight="24px"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item>
                {/* <Form.Item
                  layout="horizontal"
                  label="Enable URL login (replaces password)"
                  name="urlLogin"
                  className="right-label mb-0"
                  labelHeight="24px"
                  valuePropName="checked"
                >
                  <Checkbox />
                </Form.Item> */}
              </Col>
            )}
          </Row>
          {/* <Form buttons> */}
          <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
            <Button type="primary" htmlType="submit">
              {getHeader(role).btnText}
            </Button>
            {loggedInUser?.role === "superAdmin" && mode === "edit" && (
              <Button type="primary" className="bg-info" onClick={() => onClickChangePwd(true)} htmlType="button">
                Change Password
              </Button>
            )}
            <Button type="primary" className="bg-secondary" onClick={() => navigate(-1)} htmlType="button">
              Cancel
            </Button>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default UserForm;

UserForm.propTypes = {
  mode: PropTypes.string,
  user: PropTypes.object,
  onClickChangePwd: PropTypes.func,
};
